import React, { useState, useEffect } from 'react';
import axios from 'axios'; // Assuming you might need axios for delete
import {
    Container,
    Grid,
    Card,
    CardContent,
    CardActions,
    CardHeader,
    Button,
    Typography,
    CircularProgress,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    CardMedia
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

// --- Helper function mocks (replace with your actual implementations) ---
import { deleteScormCourse, fetchAllScormCourse } from 'helpers/scorm_helper';
import { toast } from 'react-toastify';
import { LAUNCH_COURSE } from 'helpers/url_helper';
import { generatePresignedUrlForAccess } from 'helpers/Content_helper';
const url = { API_URL: 'http://localhost:5000/api' }; // Example API URL

const ScormPackageList = () => {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for delete confirmation dialog
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [courseToDelete, setCourseToDelete] = useState(null);

    const fetchCourses = async () => {
        try {
            const data = await fetchAllScormCourse();
            if (data?.result) {
                updateThubmnail(data?.result);
            } else {
                throw new Error('No packages found.');
            }
        } catch (err) {
            console.error('Error fetching packages:', err);
            setError('Failed to load courses. Please try again later.');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchCourses();
    }, []);

    const updateThubmnail = async (data) => {
        const updateData = await Promise.all(
            data.map(async (d) => {
                // Ensure that `d.thumbnailName` exists before attempting to generate the URL
                if (d?.thumbnail) {
                    let name = d.thumbnail;
                    let type = "image";
                    const response = await generatePresignedUrlForAccess({ name, type });

                    // Check if URL generation was successful and attach the URL to the course object
                    if (response?.success) {
                        return {
                            ...d,
                            thumbnailUrl: response.presignedUrl, // Add the generated URL to the course object
                        };
                    } else {
                        console.error('Error generating pre-signed URL for', d.thumbnail);
                        return d; // Return original course object if URL generation fails
                    }
                }
                return d; // Return the course as is if thumbnailName is missing
            })
        );

        console.log(updateData)

        setPackages(updateData);
    }

    // --- Handlers ---

    const handleLaunchCourse = (course) => {
        const courseId = course.id;
        const studentId = "not required"; // replace with your actual logged-in student ID
        const type = "admin"; // or set a default type

        const url = `${LAUNCH_COURSE}/${course.storagePath}?courseId=${courseId}&studentId=${studentId}&type=${type}`;

        window.open(url, '_blank');
    };


    const handleDeleteClick = (course) => {
        setCourseToDelete(course);
        setOpenConfirmDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenConfirmDialog(false);
        setCourseToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!courseToDelete) return;
        setLoading(true);
        try {
            const data = await deleteScormCourse(courseToDelete.id);
            if (data?.success) {
                toast.success(data?.message);
                fetchCourses();
            } else {
                toast.error(data?.message);
            }

            // **Update UI state after successful deletion**


        } catch (err) {
            console.error('Error deleting course:', err);
            setError(`Failed to delete ${courseToDelete.title}.`);
        } finally {
            // Close the dialog regardless of success or failure
            handleCloseDialog();
            setLoading(false);
        }
    };


    // --- Render Logic ---

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
                <Typography variant="h6" ml={2}>Loading Courses...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Container>
                <Typography color="error" align="center" variant="h6">{error}</Typography>
            </Container>
        )
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                SCORM Courses
            </Typography>
            <Grid container spacing={4}>
                {packages.length > 0 ? (
                    packages.map(pkg => (
                        <Grid item key={pkg.id} xs={12} sm={6} md={4}>
                            <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                {/* --- Thumbnail --- */}
                                {pkg.thumbnailUrl ? (
                                    <CardMedia
                                        component="img"
                                        height="350"
                                        image={pkg.thumbnailUrl}
                                        alt={`${pkg.title} thumbnail`}
                                    />
                                ) : (
                                    <Box
                                        height="200px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            backgroundColor: '#f5f5f5',
                                            color: '#888',
                                            fontStyle: 'italic'
                                        }}
                                    >
                                        No Thumbnail
                                    </Box>
                                )}
                                <CardHeader
                                    title={pkg.title}
                                    // subheader={`Version: ${pkg.version || 'N/A'}`}
                                    action={
                                        <IconButton
                                            aria-label="delete course"
                                            onClick={() => handleDeleteClick(pkg)}
                                        >
                                            <DeleteIcon sx={{ color: 'red' }} />
                                        </IconButton>
                                    }
                                />
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {/* You can add a course description here if available */}
                                        Click 'Launch' to start the course.
                                    </Typography>
                                </CardContent>
                                <CardActions>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<RocketLaunchIcon />}
                                        onClick={() => handleLaunchCourse(pkg)}
                                    >
                                        Launch
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))
                ) : (
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" align="center">
                            No SCORM courses have been uploaded yet.
                        </Typography>
                    </Grid>
                )}
            </Grid>

            {/* Confirmation Dialog for Deletion */}
            <Dialog
                open={openConfirmDialog}
                onClose={handleCloseDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to permanently delete the course
                        <strong>"{courseToDelete?.title}"</strong>? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ScormPackageList;